import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	Paper,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import { useFormik } from "formik";
import _ from "lodash";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import { usePatchMyExpertiseFreelancerOnboardingStep } from "../../../hooks/useFreelancerProfile";
import {
	useExpertiseExperiences,
	useFreelancingYearsOfExperiences
} from "../../../hooks/useReferential";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { zoullooColors } from "../../../theme";
import { FreelancerOnboardingMyExpertiseStepViewModel } from "../../../ViewModels/FreelancerOnboardingMyExpertiseStepViewModel";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";
import { useTranslatableForm } from "../../../hooks/useTranslatableForm";
import { LanguageCode } from "../../../models/enums/LanguageCode";
import { saveFreelancerOnboardingProgression } from "../../../api/freelancer-profiles-api";
import { scheduleTranslationRefresh } from "../../../utils/translationRefresh";
import JSONPatchOperationType from "../../../utils/JSONPatchOperationType";
import TranslatableFieldWrapper from "../../translation/TranslatableFieldWrapper";
import i18n from "../../../i18n/i18n";

interface HelpTooltipProps {
	readonly tooltipText: string;
}

function HelpTooltip({ tooltipText }: HelpTooltipProps) {
	return (
		<Tooltip
			title={
				<Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
					{tooltipText}
				</Typography>
			}
			arrow
		>
			<HelpOutlineSharpIcon sx={{ ml: 1, cursor: "pointer", fontSize: 20 }} />
		</Tooltip>
	);
}

interface FormFieldWithLabelProps {
	readonly label: string;
	readonly tooltipText?: string;
	readonly children: React.ReactNode;
	readonly marginBottom?: number;
}

function FormFieldWithLabel({
	label,
	tooltipText,
	children,
	marginBottom = 3
}: FormFieldWithLabelProps) {
	return (
		<Box sx={{ mb: marginBottom }}>
			<Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
				<Typography variant="body1" sx={{ fontWeight: 500 }}>
					{label}
				</Typography>
				{tooltipText && <HelpTooltip tooltipText={tooltipText} />}
			</Box>
			{children}
		</Box>
	);
}

export default function FreelancerOnboardingMyExpertise() {
	const {
		setFreelancerOnboardingMyExpertiseStepViewModel,
		setCurrentOnboardingFormStep,
		freelancerProfileQueryResult,
		setSaveTranslationsAsync
	} = useFreelancerContext();
	const { data: freelancerProfile } = freelancerProfileQueryResult;
	const { data: expertiseExperiences } = useExpertiseExperiences();
	const { data: freelancingYearsOfExperiences } = useFreelancingYearsOfExperiences();
	const patchMyExpertiseFreelancerOnboarding =
		usePatchMyExpertiseFreelancerOnboardingStep();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	// Translatable form state for the title field
	const translatableForm = useTranslatableForm({
		entityType: "FreelancerProfile",
		entityId: freelancerProfile?.id,
		sourceLanguage: freelancerProfile?.freelancer?.user?.settings?.preferredLanguage,
		translatableFields: ["title"],
		initialValues: {
			title: freelancerProfile?.title ?? "",
		},
	});

	const translatableFormRef = useRef(translatableForm);
	translatableFormRef.current = translatableForm;

	// Expose translation save callback to the floating save button via context
	useEffect(() => {
		const saveTranslations = async () => {
			const modified = translatableFormRef.current.getModifiedLanguages();
			for (const { languageCode, fields } of modified) {
				// Send the edited value even when empty (clearing must persist); skip only fields the
				// user did not touch.
				if (!("title" in fields)) continue;
				await saveFreelancerOnboardingProgression(
					freelancerProfile!,
					[{ op: JSONPatchOperationType.Replace, path: "/title", value: fields.title }],
					languageCode.toLowerCase()
				);
			}
			scheduleTranslationRefresh(queryClient, "FreelancerProfile", freelancerProfile!.id);
		};
		setSaveTranslationsAsync(saveTranslations);
		return () => setSaveTranslationsAsync(null);
	}, [freelancerProfile, setSaveTranslationsAsync, queryClient]);

	const formValidation = Yup.object({
		title: Yup.string()
			.notRequired()
			.max(500, obj =>
				t("freelancerOnboardingMyExpertise.validation.titleMaxLength", { max: obj.max })
			),
		expertise: Yup.object().required(
			t("freelancerOnboardingMyExpertise.validation.requiredField")
		),
		experience: Yup.object().required(
			t("freelancerOnboardingMyExpertise.validation.requiredField")
		),
		freelancingYearsOfExperience: Yup.object().required(
			t("freelancerOnboardingMyExpertise.validation.requiredField")
		)
	});
	const expertises = _.uniqBy(expertiseExperiences?.map(x => x.expertise) ?? [], "id");
	const experiences = _.uniqBy(expertiseExperiences?.map(x => x.experience) ?? [], "id");
	const initialValues = {
		title: freelancerProfile?.title ?? "",
		expertise: freelancerProfile?.expertise ?? null,
		experience: freelancerProfile?.experience ?? null,
		freelancingYearsOfExperience: freelancerProfile?.freelancingYearsOfExperience ?? null,
		form: null
	} as FreelancerOnboardingMyExpertiseStepViewModel;
	const formik = useFormik<FreelancerOnboardingMyExpertiseStepViewModel>({
		initialValues,
		onSubmit: async values => {
			try {
				// Never copy another language into the FR (source) value: editing one language must
				// not overwrite another. An empty FR is fine — the worker generates it from the source.
				const submitValues = { ...values };

				await patchMyExpertiseFreelancerOnboarding.mutateAsync({
					freelancerOnboardingMyExpertiseStepViewModel: submitValues,
					freelancerProfile: freelancerProfile!,
					newStep:
						freelancerProfile?.onboarding.currentStatus ===
						FreelancerOnboardingStatus.Accepted
							? FreelancerOnboardingFormStep.MyExpertise
							: FreelancerOnboardingFormStep.MySkills
				});

				// Save edited translations for every language, including the primary one (FR).
				// Both languages are treated identically; the backend isAiTranslated flag protects
				// human-authored cells. A field is only sent if the user actually typed it.
				const modifiedLanguages = translatableFormRef.current.getModifiedLanguages();
				for (const { languageCode, fields } of modifiedLanguages) {
					// Send the edited value even when empty (clearing must persist).
					if (!("title" in fields)) continue;

					await saveFreelancerOnboardingProgression(
						freelancerProfile!,
						[{ op: JSONPatchOperationType.Replace, path: "/title", value: fields.title }],
						languageCode.toLowerCase()
					);
				}

				scheduleTranslationRefresh(queryClient, "FreelancerProfile", freelancerProfile!.id);

				if (
					freelancerProfile?.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					setCurrentOnboardingFormStep(FreelancerOnboardingFormStep.MySkills);
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		},
		validationSchema: formValidation,
		enableReinitialize: true
	});

	useEffect(() => {
		setFreelancerOnboardingMyExpertiseStepViewModel({
			...formik.values,
			form: {
				isValid: formValidation.isValidSync(formik.values),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values]);

	const tLang = i18n.getFixedT(translatableForm.editingLanguage.toLowerCase());

	return (
		<form onSubmit={formik.handleSubmit}>
			<Card
				component={Paper}
				elevation={0}
				sx={{
					marginX: "32px",
					marginY: "48px"
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Typography variant="h2" component="h1" sx={{ mb: 3, fontWeight: "bold" }}>
						{t("freelancerOnboardingMyExpertise.title")}
					</Typography>

					<TranslatableFieldWrapper
						label={t("freelancerOnboardingMyExpertise.profileTitle.label")}
						editingLanguage={translatableForm.editingLanguage}
						onLanguageChange={translatableForm.switchLanguage}
						isStale={translatableForm.isFieldStale("title")}
						uiState={translatableForm.getFieldUiState("title")}
						sourceLanguage={translatableForm.sourceLanguage}
						showLanguageSelector={translatableForm.isLanguageSelectorVisible("title")}
						showAiFirstNote={translatableForm.isAiFirstNoteVisible("title")}
						isDraft={translatableForm.isFieldDraft("title")}
						onBeginDraft={() => translatableForm.beginFieldDraft("title")}
						onCancelDraft={() => {
							const restored = translatableForm.cancelFieldDraft("title");
							// Formik mirrors the FR value live while the draft is typed: resync on cancel.
							if (translatableForm.editingLanguage === LanguageCode.Fr) {
								formik.setFieldValue("title", restored);
							}
						}}
						canHandBackToAi={translatableForm.isFieldHandBackable("title")}
						isRegenerating={translatableForm.isFieldRegenerating("title")}
						onEnableAi={() => translatableForm.enableAiForField("title")}
						contentReadyToken={translatableForm.getFieldContentToken("title")}
					>
						<TextField
							name="title"
							fullWidth
							placeholder={tLang("freelancerOnboardingMyExpertise.profileTitle.placeholder")}
							variant="standard"
							// Native read-only carries the AI lock (the wrapper only dims): text stays
							// selectable/copiable and accessible, unlike the old pointer-events tarp.
							InputProps={{ readOnly: translatableForm.isFieldReadOnly("title") }}
							value={translatableForm.getFieldValue("title")}
							onChange={(e) => {
								translatableForm.setFieldValue("title", e.target.value);
								if (translatableForm.editingLanguage === LanguageCode.Fr) {
									formik.setFieldValue("title", e.target.value);
								}
							}}
							onBlur={() => formik.setFieldTouched("title", true)}
							error={formik.touched.title && Boolean(formik.errors.title)}
							helperText={formik.touched.title && formik.errors.title}
							sx={{ mb: 2 }}
						/>
					</TranslatableFieldWrapper>

					<FormFieldWithLabel
						label={t("freelancerOnboardingMyExpertise.expertise.label")}
					>
						<Autocomplete
							isOptionEqualToValue={(option, value) => option.id === value.id}
							options={expertises ?? []}
							groupBy={option => option.category}
							getOptionLabel={e => t(`Expertises.${e.label}`)}
							value={formik.values.expertise}
							onChange={(_, newValue) => {
								formik.setFieldValue("expertise", newValue);
							}}
							onBlur={() => formik.setTouched({ expertise: true })}
							disabled={
								freelancerProfile?.onboarding.currentStatus !==
								FreelancerOnboardingStatus.NotSubmitted
							}
							renderInput={params => (
								<TextField
									{...params}
									placeholder={t("freelancerOnboardingMyExpertise.expertise.placeholder")}
									variant="outlined"
									error={formik.touched.expertise && Boolean(formik.errors.expertise)}
									helperText={formik.touched.expertise && formik.errors.expertise}
									sx={{
										width: "45%"
									}}
								/>
							)}
							sx={{ mb: 2 }}
						/>
					</FormFieldWithLabel>

					<FormFieldWithLabel
						label={t("freelancerOnboardingMyExpertise.experience.label")}
					>
						<Autocomplete
							isOptionEqualToValue={(option, value) => option.id === value.id}
							options={experiences ?? []}
							getOptionLabel={e => t(`Experiences.${e.label}`)}
							value={formik.values.experience}
							onChange={(_, newValue) => {
								formik.setFieldValue("experience", newValue);
							}}
							onBlur={() => formik.setTouched({ experience: true })}
							disabled={
								freelancerProfile?.onboarding.currentStatus !==
								FreelancerOnboardingStatus.NotSubmitted
							}
							renderInput={params => (
								<TextField
									{...params}
									placeholder={t(
										"freelancerOnboardingMyExpertise.experience.placeholder"
									)}
									variant="outlined"
									error={formik.touched.experience && Boolean(formik.errors.experience)}
									helperText={formik.touched.experience && formik.errors.experience}
									sx={{
										width: "45%"
									}}
								/>
							)}
							sx={{ mb: 2 }}
						/>
					</FormFieldWithLabel>

					<FormFieldWithLabel
						label={t("freelancerOnboardingMyExpertise.freelancingYears.label")}
						marginBottom={4}
					>
						<Autocomplete
							isOptionEqualToValue={(option, value) => option.id === value.id}
							options={freelancingYearsOfExperiences ?? []}
							getOptionLabel={f => t(`FreelancingYearsOfExperiences.${f.label}`)}
							value={formik.values.freelancingYearsOfExperience}
							onChange={(_, newValue) =>
								formik.setFieldValue("freelancingYearsOfExperience", newValue)
							}
							onBlur={() => formik.setTouched({ freelancingYearsOfExperience: true })}
							renderInput={params => (
								<TextField
									{...params}
									placeholder={t(
										"freelancerOnboardingMyExpertise.freelancingYears.placeholder"
									)}
									variant="outlined"
									error={
										formik.touched.freelancingYearsOfExperience &&
										Boolean(formik.errors.freelancingYearsOfExperience)
									}
									helperText={
										formik.touched.freelancingYearsOfExperience &&
										formik.errors.freelancingYearsOfExperience
									}
									sx={{
										width: "45%"
									}}
								/>
							)}
							sx={{ mb: 2 }}
						/>
					</FormFieldWithLabel>
					<Button
						type="submit"
						variant="contained"
						fullWidth
						size="large"
						disabled={!formValidation.isValidSync(formik.values) || formik.isSubmitting}
						sx={{
							backgroundColor: zoullooColors.secondary.main,
							width: "45%",
							color: zoullooColors.secondary.contrastText,
							py: 1.5
						}}
					>
						{freelancerProfile?.onboarding.currentStatus !==
						FreelancerOnboardingStatus.Accepted
							? t("freelancerOnboardingMyExpertise.submitButton")
							: t("common.save")}
					</Button>
				</CardContent>
			</Card>
		</form>
	);
}
